import { COMMUNITY_URL } from '@/constants/links';
import { REFERRAL_COPY } from '@/constants/referrals';

/**
 * Invite Title and Message preview:
 *
 * Invite your colleagues to Primum
 * I use Primum to curbside expert oncologists and get answers in under 24 hours.
 * It's free for NPI-verified providers, and you'll get $100 when you sign up with this link
 * and ask your first question within 30 days. You can also direct your $100 to a charity of your
 * choice.
 * You can even ask anonymously, and there are paid survey opportunities too.
 * https://app.primum.co/create-account?referred_by=ABC123
 */
export const INVITE_SHARE = {
  TITLE: 'Invite your colleagues to Primum',
  MESSAGE: REFERRAL_COPY.invitee.shareLinkMessage,
};

/**
 * Constructs the complete invite share message with personalized link
 * @param inviteLink - The personalized invite link (e.g., "https://app.primum.co/create-account?referred_by=ABC123")
 * @returns Complete message with invite link on its own line
 */
export const getInviteMessageWithLink = (inviteLink: string) =>
  `${INVITE_SHARE.MESSAGE}\n\n${inviteLink}`;

/**
 * Adds UTM parameters to a referral link for tracking
 * @param baseLink - The base referral link
 * @param utmSource - The UTM source ('share_link', 'copy_link', 'email_invite')
 * @returns Link with UTM parameters added
 */
export const addUtmToInviteLink = (baseLink: string, utmSource: string) => {
  const url = new URL(baseLink);

  url.searchParams.set('utm_source', utmSource);

  return url.toString();
};

/**
 * Community Post Share Title and Message preview:
 *
 * This discussion might help
 * I found this helpful on Primum, where oncology providers get expert advice in under 24 hours.
 * The insights here might be valuable for your practice:
 *
 * https://app.primum.co/community/6985
 */
export const COMMUNITY_POST_SHARE = {
  TITLE: 'This discussion might help',
  MESSAGE:
    'I found this helpful on Primum, where oncology providers get expert advice in under 24 hours. The insights here might be valuable for your practice:',
  URL: COMMUNITY_URL,
};

/**
 * Constructs the complete community post share message with post link
 * @param postUrl - The complete post URL (e.g., "https://app.primum.co/community/6985")
 * @returns Complete message with post link on its own line
 */
export const getCommunityPostMessageWithLink = (postUrl: string) =>
  `${COMMUNITY_POST_SHARE.MESSAGE}\n\n${postUrl}`;

/**
 * Email template for invitations
 */
export const INVITE_EMAIL = {
  SUBJECT: "You're invited to join Primum",
  SENDER_NAME: 'Primum',
  SENDER_EMAIL: 'hello@primum.co',
  REPLY_TO: 'support@primum.co',
  BODY: `Dr. {senderName} uses Primum for curbside consults.

${REFERRAL_COPY.invitee.inviteEmailIncentiveLine}

Primum connects NPI-verified providers to over 100 oncology specialists across the US for free 1:1 advice in under 24 hours. You can even ask questions anonymously. Members also receive paid survey opportunities.

{inviteLink}

Best Regards,
The Primum Team

Questions? Reply to this email or reach out at support@primum.co`,
};

/**
 * Constructs the complete invite email body with personalized content
 * @param senderName - The first name of the person sending the invite
 * @param inviteLink - The personalized invite link
 * @returns Complete email body with personalized content
 */
export const getInviteEmailBody = (senderName: string, inviteLink: string) => {
  const replacements = {
    '{senderName}': senderName,
    '{inviteLink}': inviteLink,
  };

  return Object.entries(replacements).reduce(
    (body, [placeholder, value]) => body.replace(new RegExp(placeholder, 'g'), value),
    INVITE_EMAIL.BODY
  );
};
